import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@chainlit/react-client';
import { useTranslation } from 'react-i18next';
import AdminSidebar from '@/components/admin/AdminSidebar';
import AdminHeader from '@/components/admin/AdminHeader';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

export default function Admin() {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const { i18n } = useTranslation();
  const isEnglish = i18n.language.toLowerCase().startsWith('en');

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    async function checkAccess() {
      if (!user?.identifier) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      try {
        // Identity comes from the session, not the body — this endpoint reports
        // only the caller's own status, so it cannot be used to enumerate others.
        const response = await fetch('/api/user-access/me');

        if (!response.ok) {
          setHasAccess(false);
          return;
        }

        // A 200 that isn't JSON means the request never reached the endpoint —
        // Chainlit's SPA catch-all served index.html instead. That once read as a
        // denial and showed "Access Denied" to real admins, so name it loudly
        // rather than silently mistaking a routing bug for a permissions answer.
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error(
            `/api/user-access/me returned "${contentType}" instead of JSON — the route is being shadowed, not denying access.`
          );
        }

        const data = await response.json();
        setHasAccess(data.success ? data.has_access : false);
      } catch {
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    }

    checkAccess();
  }, [user]);

  // Loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">
            {isEnglish ? 'Checking access...' : 'Provera pristupa...'}
          </p>
        </div>
      </div>
    );
  }

  // Access denied
  if (!hasAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="max-w-md w-full mx-4">
          <div className="bg-card border border-border rounded-lg shadow-lg p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <ShieldAlert className="h-12 w-12 text-destructive" />
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-2">
              {isEnglish ? 'Access denied' : 'Pristup nije dozvoljen'}
            </h1>
            <p className="text-muted-foreground mb-4">
              {isEnglish
                ? "You don't have permission to access the administrator panel."
                : 'Nemaš dozvolu za pristup administratorskom panelu.'}
            </p>
            <div className="bg-muted rounded-md p-4 mb-4">
              <p className="text-sm text-muted-foreground mb-1">
                {isEnglish ? 'Your account:' : 'Tvoj nalog:'}
              </p>
              <p className="font-mono text-sm">{user?.identifier || 'Unknown'}</p>
            </div>
            <p className="text-sm text-muted-foreground">
              {isEnglish
                ? 'Contact an administrator if you believe this is an error.'
                : 'Obrati se administratoru ako smatraš da je ovo greška.'}
            </p>
            <div className="mt-6">
              <a 
                href="/"
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary-hover h-10 px-4 py-2"
              >
                {isEnglish ? 'Return to chat' : 'Nazad na razgovor'}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Access granted - show admin panel
  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar open={sidebarOpen} setOpen={setSidebarOpen} isMobile={isMobile} />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <AdminHeader
          isMobile={isMobile}
          onToggleSidebar={() => setSidebarOpen(prev => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <div className="flex-1 overflow-y-auto">
          <div
            className="mx-auto w-full max-w-[1600px] px-6 py-8 lg:px-8"
            data-testid="admin-page-shell"
          >
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
